// src/components/common/NotFoundState.tsx
/**
 * Shown on a detail page when the record it's trying to load doesn't exist — most often because it
 * was just deleted (from this screen, from the list's row action, or by someone else) and the page is
 * still holding the old id in the URL.
 *
 * Deliberately generic: every detail page (case, registration, request, enrolment, …) hits this same
 * shape, so the entity name and back-navigation are the only things that change per use.
 */
import { Stack, Text, Button, ThemeIcon, Card } from '@mantine/core';
import { IconSearchOff, IconArrowLeft, IconCircleX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export interface NotFoundStateProps {
  /** Singular noun — "case", "registration", "request", "enrolment". */
  entityLabel: string;
  /** Where "Back to X" should go. */
  backTo: string;
  /** Defaults to `Back to {entityLabel}s`. Override for irregular plurals. */
  backLabel?: string;
  /**
   * Optional extra line under the headline — e.g. explaining *why* it's likely gone
   * ("It may have been deleted."). Left out by default since the reason isn't always known.
   */
  detail?: string;
}

export default function NotFoundState({ entityLabel, backTo, backLabel, detail }: NotFoundStateProps) {
  const navigate = useNavigate();

  return (
    <Card withBorder radius="md" p="xl" shadow="sm" mt={160}>
    <Stack align="center" gap="sm" py={80}>
      <ThemeIcon size={56} radius="xl" variant="light" color="gray">
        <IconCircleX size={28} />
      </ThemeIcon>

      <Text size="lg" fw={600}>
        {entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)} not found
      </Text>

      {detail && (
        <Text size="sm" c="dimmed" ta="center" maw={360}>
          {detail}
        </Text>
      )}

      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(backTo)}
        mt="xs"
      >
        {backLabel ?? `Back to ${entityLabel}s`}
      </Button>
    </Stack>
    </Card>
  );
}